import React, { createContext, useContext, useState, useEffect } from "react";
import { fetchTenantInfo, isTenantResolutionFailed } from "../api/tenantApi";

const TenantContext = createContext();

// Fallback when the tenant has no logo of its own: the deployment's env logo if
// set, else the bundled default. One fallback chain everywhere in the app
// (tenant logo -> env logo -> bundled default). TODO: replace with per-tenant logo.
const DEFAULT_LOGO = process.env.REACT_APP_LOGO
  ? `${process.env.PUBLIC_URL || ""}/${process.env.REACT_APP_LOGO}`
  : "/logo.png";

// Remember the last resolved tenant per host so the first paint (e.g. the sidebar
// logo) already shows the tenant's branding instead of the default while
// tenant-info is still being fetched. Keyed by host so one tenant's cache never
// bleeds into another's. The network fetch still runs and reconciles every load.
const TENANT_CACHE_PREFIX = "tvid.admin.tenant.";
const currentHost = () =>
  typeof window !== "undefined" ? window.location.hostname : "";

function readCachedTenant(host) {
  try {
    if (!host || typeof window === "undefined") return null;
    const raw = window.localStorage.getItem(TENANT_CACHE_PREFIX + host);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null; // privacy mode / bad JSON — fall back to default
  }
}

function writeCachedTenant(host, tenantObj) {
  try {
    if (!host || typeof window === "undefined" || !tenantObj) return;
    window.localStorage.setItem(TENANT_CACHE_PREFIX + host, JSON.stringify(tenantObj));
  } catch {
    /* ignore storage errors (quota / privacy mode) */
  }
}

export const TenantProvider = ({ children }) => {
  const [tenant, setTenant] = useState(() => readCachedTenant(currentHost()));
  const [tenantNotFound, setTenantNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await fetchTenantInfo();
      if (cancelled) return;
      setTenant(data);
      setTenantNotFound(isTenantResolutionFailed());
      // Only cache a real resolution — a transient fetch failure returns null,
      // and we'd rather keep showing the last-good logo than blank it.
      writeCachedTenant(currentHost(), data);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const tenantSuspended = tenant?.status === "SUSPENDED";

  const logoUrl = tenant?.logoUrl || null;

  /** Whether the tenant's active plan includes a given feature key (e.g. "content_partners_portal"). */
  const hasFeature = (key) =>
    Array.isArray(tenant?.features) && tenant.features.includes(key);

  if (tenantNotFound || tenantSuspended) {
    const title = tenantSuspended ? "Tenant suspended" : "Tenant not found";
    const message = tenantSuspended
      ? "This tenant account has been suspended. Please contact support for assistance."
      : "This subdomain isn't associated with any active tenant. It may have been removed, or the URL may be incorrect.";
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          textAlign: "center",
          padding: "24px",
          background: "#0f0f10",
          color: "#f5f5f5",
        }}
      >
        <h1 style={{ fontSize: "1.5rem", marginBottom: "8px" }}>{title}</h1>
        <p style={{ color: "#a3a3a3", maxWidth: 420 }}>{message}</p>
      </div>
    );
  }

  return (
    <TenantContext.Provider
      value={{
        tenant,
        logoUrl,
        /** Logo to display: tenant logo if set, else default */
        logo: logoUrl || DEFAULT_LOGO,
        hasFeature,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => useContext(TenantContext);
